import { ApolloQueryResult, TypedDocumentNode } from '@apollo/client';
import gql from 'graphql-tag';
import { Dispatch, SetStateAction } from 'react';
import { Person, QuerySearchSocialProfileArgs, SearchSocialProfileResponse } from 'sr-types/lib/person/v1/graphql';
import { AlternateIdValidationInput } from '../../../common/components/AlternateIdForm';
import { LOCATION_FIELDS } from '../../../common/components/location';
import { client, getConflictedUserDetails } from '../../../common/components/person/person';
import { FETCH_POLICY_CONFIG } from '../../../common/constants/constants';
import { ContactInfoFragment, ReferenceFragment } from '../../../common/list/fragments';
import { ValidationRulesType } from '../../../production/helpers/productionUtils';

export type AlternateIdProps = {
    conflictedPerson: Person;
    setConflictedPerson: Dispatch<SetStateAction<Person>>;
    errorKey: string;
    setErrorKey: Dispatch<SetStateAction<string>>;
    fieldSelected: string;
    setFieldSelected: Dispatch<SetStateAction<string>>;
    conflictedProfileId: string;
    setConflictedProfileId: Dispatch<SetStateAction<string>>;
    setIsSaveDisabled: Dispatch<SetStateAction<boolean>>;
    setDynamicValidationRules: Dispatch<SetStateAction<ValidationRulesType>>;
};

export const searchSocialProfile: TypedDocumentNode<SearchSocialProfileResponse, QuerySearchSocialProfileArgs> = gql`
    query SearchSocialProfile($input: SearchSocialProfileInput!) {
        searchSocialProfile(input: $input) {
            errors {
                field
                key
                message
            }
            alternateId {
                label
                value
                url
            }
            person {
                identity {
                    id
                }
                name {
                    firstName
                    lastName
                }
                alternateIds {
                    label
                    value
                    url
                }
                deiInfo {
                    birthYear
                    disabilityStatus
                    ethnicity
                    gender
                    socioEconomicStatus
                }
                memberships {
                    organizationReference {
                        ...Reference
                    }
                }
                profileImage
                profileImageReference {
                    id
                    label
                }
                summary {
                    headline
                    about
                }
                contactInfo {
                    ...ContactInfo
                }
                professionalRoles
                location {
                    ...LocationFields
                }
                reference {
                    ...Reference
                }
                security {
                    allowedActions
                }
            }
        }
    }
    ${LOCATION_FIELDS}
    ${ReferenceFragment}
    ${ContactInfoFragment}
`;

export const handleFetchingConflictedPersonDetailsResponse = (
    email: string,
    ownerId: string,
    onSuccess: (res) => void,
    onError: () => void
) => {
    getConflictedUserDetails(
        {
            page: { from: 0, size: 2000 },
            filters: [
                { identifier: 'entity', value: 'Person' },
                {
                    identifier: 'email',
                    value: email
                }
            ]
        },
        ownerId
    )
        .then((res) => {
            onSuccess(res);
        })
        .catch(() => {
            onError();
        });
};

export const extractPersonDetailsFromSearchQuery = (person) => {
    const { name, organization, role, production, productionRole } = person;
    return {
        Name: name,
        Organization: organization,
        Role: role,
        Production: production,
        ProductionRole: productionRole
    };
};

export const getValidationErrors = ({
    profileUrl,
    profileId,
    entityId,
    ownerId
}: AlternateIdValidationInput): Promise<ApolloQueryResult<SearchSocialProfileResponse>> => {
    return client.query({
        query: searchSocialProfile,
        variables: {
            input: {
                profileUrl,
                profileId,
                personId: entityId
            }
        },
        context: {
            headers: {
                ownerId
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
    });
};
